import { useEffect, useState } from "react";
import { LOADING_EXIT_DURATION_MS } from "@/components/Loading/LoadingOverlay";

export type LoadingPhase = "loading" | "exit" | "done";

/**
 * ローディング終了時に「余韻」を挟むためのフェーズ管理フック。
 * LoadingOverlay とセットで使う想定の共通ロジック。
 * - loading: isLoading が true の間
 * - exit: ローディングが成功で終わった直後の余韻（exitDuration の間だけ）
 * - done: 余韻が終わった後（エラー終了時は余韻を挟まず直接 done）
 * useEffect での切り替えだと完了後の画面が1フレーム先に描画されてちらつくため、
 * レンダー中に終了を検知して余韻フェーズへ入る。
 * @param isLoading ローディング中かどうか
 * @param showExit 完了時に余韻を見せるか（エラー終了時は false を渡す）
 * @param exitDuration 余韻の長さ（ミリ秒）。オーバーレイのクロスフェードの長さと合わせる。
 */
export const useLoadingPhase = (
  isLoading: boolean,
  showExit: boolean,
  exitDuration = LOADING_EXIT_DURATION_MS
): LoadingPhase => {
  const [phase, setPhase] = useState<LoadingPhase>(
    isLoading ? "loading" : "done"
  );
  if (isLoading && phase !== "loading") {
    setPhase("loading");
  }
  if (!isLoading && phase === "loading") {
    setPhase(showExit ? "exit" : "done");
  }

  useEffect(() => {
    if (phase !== "exit") {
      return;
    }
    const timer = setTimeout(() => {
      setPhase("done");
    }, exitDuration);
    return () => {
      clearTimeout(timer);
    };
  }, [phase, exitDuration]);

  return phase;
};
